import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchEmailSignatures,
  fetchDefaultEmailSignature,
  createEmailSignature,
  updateEmailSignature,
  deleteEmailSignature,
  EmailSignatureInput,
} from '@/services/api/emailSignatures';
import { toast } from '@/hooks/use-toast';
import { requestDeduplication } from '@/services/requestDeduplication';

const QUERY_KEY = 'emailSignatures';

export const useEmailSignatures = () => {
  const queryClient = useQueryClient();

  const {
    data: signatures = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: fetchEmailSignatures,
  });

  const invalidate = () => {
    // Drop cached GETs so the refetch hits the server, not the 90s dedup cache
    requestDeduplication.clearByUrl('/email/signatures');
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
  };

  const createMutation = useMutation({
    mutationFn: createEmailSignature,
    onSuccess: () => {
      invalidate();
      toast({
        title: 'Signature created',
        description: 'Your email signature has been created successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create signature',
        description: error.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<EmailSignatureInput> }) =>
      updateEmailSignature(id, data),
    onSuccess: () => {
      invalidate();
      toast({
        title: 'Signature updated',
        description: 'Your email signature has been updated successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update signature',
        description: error.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteEmailSignature,
    onSuccess: () => {
      invalidate();
      toast({
        title: 'Signature deleted',
        description: 'Your email signature has been deleted successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete signature',
        description: error.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
  });

  return {
    signatures,
    isLoading,
    error,
    refetch,
    createSignature: createMutation.mutateAsync,
    updateSignature: updateMutation.mutateAsync,
    deleteSignature: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};

/**
 * The user's default signature — used to pre-select on a brand-new compose.
 */
export const useDefaultEmailSignature = () => {
  return useQuery({
    queryKey: [QUERY_KEY, 'default'],
    queryFn: fetchDefaultEmailSignature,
    staleTime: 5 * 60 * 1000,
  });
};
